import { BusinessOutlined } from "@mui/icons-material";
import AccessTimeFilledOutlinedIcon from "@mui/icons-material/AccessTimeFilledOutlined";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AttachMoneyIconOutlined from "@mui/icons-material/AttachMoneyOutlined";
import AssessmentIconOutlined from "@mui/icons-material/AssessmentOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import HandymanIcon from '@mui/icons-material/Handyman';
import HelpIcon from "@mui/icons-material/Help";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import HttpsIcon from "@mui/icons-material/Https";
import PackageIcon from "@mui/icons-material/Inventory";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { usePackageAccess } from "../../../Admin/Packages/hooks/usePackageAccess";
import { Box, Collapse, Badge } from "@mui/material";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { generatePath } from "react-router";
import { Link, useLocation } from "react-router-dom";
import {
  selectCanCreateProject,
  selectHeiUser,
} from "../../../Auth/store/accountSlice";
import routes from "../../../Common/consts/routes";
import MenuFooter from "../MenuFooter/MenuFooter";
import MenuHeader from "../MenuHeader/MenuHeader";
import { usePendingRequestsCount } from "../../../Projects/SelfOnboarding/hooks/usePendingRequestsCount";
import styles from "./Menu.module.scss";

interface IMenuButton {
  collapsed: boolean;
  Icon: any;
  text: string;
  path?: string;
  isActive: boolean;
  badgeCount?: number;
}

const MenuButton: React.FunctionComponent<IMenuButton> = ({
  collapsed,
  Icon,
  text,
  path,
  isActive,
  badgeCount,
}) => {
  return (
    <Link to={path ?? "/not-found"}>
      <ListItemButton
        className={classNames(styles.link, { [styles.activeLink]: isActive })}
      >
        <ListItemIcon className={classNames({ [styles.linkIcon]: collapsed })}>
          {badgeCount !== undefined && badgeCount > 0 ? (
            <Badge badgeContent={badgeCount} color="error">
              <Icon />
            </Badge>
          ) : (
            <Icon />
          )}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            sx={{ fontFamily: "Lucida Sans Unicode !important" }}
            primary={text}
          />
        )}
      </ListItemButton>
    </Link>
  );
};

const menuButtons = [
  {
    Icon: HomeRoundedIcon,
    text: "Projects",
    path: generatePath(routes.projects, { tab: "my" }),
  },
  {
    Icon: AddCircleIcon,
    text: "New project",
    path: routes.createProject,
  },
  {
    Icon: HttpsIcon,
    text: "Request access",
    path: generatePath(routes.projects, { tab: "other" }),
  },
  {
    Icon: AccessTimeFilledOutlinedIcon,
    text: "Requests",
    path: generatePath(routes.requests, { tab: "pending" }),
  },
  {
    Icon: HelpIcon,
    text: "FAQ",
    path: routes.faq,
  },
];

const adminMenuButtons = [
  {
    Icon: BusinessOutlined,
    text: "OpCo's",
    path: routes.admin.opcos,
  },
  {
    Icon: HandymanIcon,
    text: "Tools",
    path: routes.admin.tools,
  },
  {
    Icon: PackageIcon,
    text: "Packages",
    path: routes.admin.packages,
  },
  {
    Icon: AssessmentIconOutlined,
    text: "Reports",
    path: routes.admin.reports,
  },
  {
    Icon: BuildCircleIcon,
    text: "Operations",
    path: routes.admin.operationsFigmaCleanup,
  },
  {
    Icon: AttachMoneyIconOutlined,
    text: "Download billing",
    path: routes.admin.billing,
  },
];

interface MenuProps {
  collapsed: boolean;
  setCollapsed: (collasped: boolean) => void;
}
const Menu = ({ collapsed, setCollapsed }: MenuProps) => {
  const location = useLocation();
  const heiUser = useSelector(selectHeiUser);
  const canCreateProject = useSelector(selectCanCreateProject);
  const { count: pendingRequestsCount } = usePendingRequestsCount();

  const isAdmin = useMemo(
    () => heiUser.roles.some((r) => r === "Admin"),
    [heiUser]
  );
  const { hasPackageAccess } = usePackageAccess();
  const showPackagesLink = !isAdmin && hasPackageAccess;

  const [adminSectionOpen, setOpen] = useState(true);
  // const [collapsed, setCollapsed] = useState(false);

  const handleClick = () => {
    setOpen(!adminSectionOpen);
  };

  const isActiveLink = (path: string) => {
    return location.pathname.includes(path);
  };

  const isNewProjectButton = (text: string) => text === "New project";

  return (
    <Box
      className={`${styles.menuContainer} ${
        collapsed ? styles.menuContainerCollapsed : ""
      }`}
    >
      <List
        sx={{
          bgcolor: "background.paper",
          height: "100%",
          p: 0,
        }}
        component="nav"
        aria-labelledby="nested-list-subheader"
      >
        <MenuHeader collapsed={collapsed} />

        <div className={styles.navigationElements}>
          <div>
            {menuButtons.map((mb, i) => (
              <>
                {!isNewProjectButton(mb.text) && (
                  <MenuButton
                    key={i}
                    collapsed={collapsed}
                    Icon={mb.Icon}
                    text={mb.text}
                    path={mb.path}
                    isActive={isActiveLink(mb.path)}
                    badgeCount={mb.text === "Requests" ? pendingRequestsCount : undefined}
                  />
                )}
                {isNewProjectButton(mb.text) && canCreateProject && (
                  <MenuButton
                    key={i}
                    collapsed={collapsed}
                    Icon={mb.Icon}
                    text={mb.text}
                    path={mb.path}
                    isActive={isActiveLink(mb.path)}
                  />
                )}
              </>
            ))}

            {showPackagesLink && (
              <MenuButton
                collapsed={collapsed}
                Icon={PackageIcon}
                text="Packages"
                path={routes.admin.packages}
                isActive={isActiveLink(routes.admin.packages)}
              />
            )}

            {isAdmin && (
              <>
                <ListItemButton onClick={handleClick}>
                  <ListItemIcon>
                    <EngineeringOutlinedIcon />
                  </ListItemIcon>
                  <Collapse in={!collapsed} orientation="horizontal">
                    <ListItemText primary="Admin" />
                  </Collapse>

                  {adminSectionOpen ? (
                    <ExpandLessOutlinedIcon />
                  ) : (
                    <ExpandMoreOutlinedIcon />
                  )}
                </ListItemButton>
                <Collapse in={adminSectionOpen} timeout="auto" unmountOnExit sx={{ pl: 2 }}>
                  <List component="div" disablePadding>
                    {adminMenuButtons.map((mb, i) => (
                      <MenuButton
                        key={i}
                        collapsed={collapsed}
                        Icon={mb.Icon}
                        text={mb.text}
                        path={mb.path}
                        isActive={isActiveLink(mb.path)}
                      />
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </div>
          <MenuFooter setCollapsed={setCollapsed} collapsed={collapsed} />
        </div>
      </List>
    </Box>
  );
};

export default Menu;
